import { useEffect, useRef, useState, useCallback } from 'react';
import { useUser } from '@/contexts/UserContext';
import { updateUserLocation } from '@/lib/services/locationService';
import { useToast } from '@/hooks/use-toast';

interface LocationState {
  latitude: number | null;
  longitude: number | null;
  accuracy: number | null;
  timestamp: number | null;
  error: string | null;
  isTracking: boolean;
  permissionGranted: boolean | null;
}

interface UseLocationTrackingOptions {
  /**
   * Enable continuous tracking (default: false)
   * When true, uses watchPosition instead of getCurrentPosition
   */
  continuous?: boolean;
  
  /**
   * Minimum distance in meters before updating location in database (default: 100m)
   * Helps reduce unnecessary API calls
   */
  minDistanceMeters?: number;
  
  /**
   * Interval in milliseconds to update location in database (default: 5 minutes)
   * Even if user hasn't moved, update location periodically
   */
  updateIntervalMs?: number;
  
  /**
   * Enable high accuracy GPS (default: true)
   * Uses more battery but provides better accuracy
   */
  enableHighAccuracy?: boolean;
  
  /**
   * Timeout in milliseconds for location request (default: 10000)
   */
  timeout?: number;
  
  /**
   * Maximum age of cached location in milliseconds (default: 300000 = 5 minutes)
   */
  maximumAge?: number;
  
  /**
   * Auto-start tracking when hook mounts (default: false)
   */
  autoStart?: boolean;
}

const DEFAULT_OPTIONS: Required<UseLocationTrackingOptions> = {
  continuous: false,
  minDistanceMeters: 100,
  updateIntervalMs: 5 * 60 * 1000, // 5 minutes
  enableHighAccuracy: true,
  timeout: 10000,
  maximumAge: 300000,
  autoStart: false,
};

export function useLocationTracking(options: UseLocationTrackingOptions = {}) {
  const { user } = useUser();
  const { toast } = useToast();
  const opts = { ...DEFAULT_OPTIONS, ...options };
  
  const [location, setLocation] = useState<LocationState>({
    latitude: null,
    longitude: null,
    accuracy: null,
    timestamp: null,
    error: null,
    isTracking: false,
    permissionGranted: null,
  });

  const watchIdRef = useRef<number | null>(null);
  const lastUpdateRef = useRef<{ lat: number; lng: number; time: number } | null>(null);
  const updateIntervalRef = useRef<number | null>(null);
  const isUpdatingRef = useRef(false);
  const latestLocationRef = useRef<{ latitude: number; longitude: number } | null>(null);

  const updateLocationInDatabase = useCallback(async (lat: number, lng: number) => {
    if (!user?.id || isUpdatingRef.current) return;
    
    isUpdatingRef.current = true;
    try {
      await updateUserLocation(user.id, lat, lng);
      lastUpdateRef.current = { lat, lng, time: Date.now() };
    } catch (error) {
      console.error('Failed to update location in database:', error);
      // Don't show toast for every error to avoid spam
    } finally {
      isUpdatingRef.current = false;
    }
  }, [user?.id]);

  const shouldUpdateLocation = useCallback((lat: number, lng: number): boolean => {
    const now = Date.now();
    const lastUpdate = lastUpdateRef.current;
    
    // Always update if no previous update
    if (!lastUpdate) return true;
    
    // Check if enough time has passed
    const timeSinceUpdate = now - lastUpdate.time;
    if (timeSinceUpdate >= opts.updateIntervalMs) return true;
    
    // Check if moved enough distance
    const distance = calculateDistance(
      lastUpdate.lat,
      lastUpdate.lng,
      lat,
      lng
    );
    
    return distance >= opts.minDistanceMeters;
  }, [opts.updateIntervalMs, opts.minDistanceMeters]);

  const handleLocationSuccess = useCallback((position: GeolocationPosition) => {
    const { latitude, longitude, accuracy } = position.coords;
    const timestamp = position.timestamp;
    
    setLocation(prev => ({
      ...prev,
      latitude,
      longitude,
      accuracy,
      timestamp,
      error: null,
      permissionGranted: true,
    }));

    latestLocationRef.current = { latitude, longitude };

    // Update database if location changed significantly
    if (shouldUpdateLocation(latitude, longitude)) {
      updateLocationInDatabase(latitude, longitude);
    }
  }, [shouldUpdateLocation, updateLocationInDatabase]);

  const handleLocationError = useCallback((error: GeolocationPositionError) => {
    let errorMessage = 'Location access denied';
    
    switch (error.code) {
      case error.PERMISSION_DENIED:
        errorMessage = 'Location permission denied. Please enable location access in your browser settings.';
        break;
      case error.POSITION_UNAVAILABLE:
        errorMessage = 'Location information unavailable.';
        break;
      case error.TIMEOUT:
        errorMessage = 'Location request timed out.';
        break;
    }
    
    setLocation(prev => ({
      ...prev,
      error: errorMessage,
      permissionGranted: error.code === error.PERMISSION_DENIED ? false : null,
      isTracking: false,
    }));
  }, []);

  const startTracking = useCallback(() => {
    if (!navigator.geolocation) {
      setLocation(prev => ({
        ...prev,
        error: 'Geolocation is not supported by your browser.',
        permissionGranted: false,
      }));
      return;
    }

    if (watchIdRef.current !== null) {
      // Already tracking
      return;
    }

    const geoOptions: PositionOptions = {
      enableHighAccuracy: opts.enableHighAccuracy,
      timeout: opts.timeout,
      maximumAge: opts.maximumAge,
    };

    if (opts.continuous) {
      // Continuous tracking with watchPosition
      watchIdRef.current = navigator.geolocation.watchPosition(
        handleLocationSuccess,
        handleLocationError,
        geoOptions
      );
      
      setLocation(prev => ({ ...prev, isTracking: true }));
    } else {
      // One-time location request
      navigator.geolocation.getCurrentPosition(
        handleLocationSuccess,
        handleLocationError,
        geoOptions
      );
    }

    // Set up periodic updates even if location hasn't changed
    if (opts.continuous) {
      updateIntervalRef.current = window.setInterval(() => {
        const current = latestLocationRef.current;
        if (current) {
          updateLocationInDatabase(current.latitude, current.longitude);
        }
      }, opts.updateIntervalMs);
    }
  }, [
    opts.continuous,
    opts.enableHighAccuracy,
    opts.timeout,
    opts.maximumAge,
    opts.updateIntervalMs,
    handleLocationSuccess,
    handleLocationError,
    updateLocationInDatabase,
  ]);

  const stopTracking = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    
    if (updateIntervalRef.current !== null) {
      clearInterval(updateIntervalRef.current);
      updateIntervalRef.current = null;
    }
    
    setLocation(prev => ({ ...prev, isTracking: false }));
  }, []);

  const requestPermission = useCallback(async () => {
    if (!navigator.geolocation) {
      toast({
        title: 'Location not supported',
        description: 'Your browser does not support geolocation.',
        variant: 'destructive',
      });
      return false;
    }

    try {
      // Request permission by attempting to get location
      await new Promise<void>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          () => resolve(),
          reject,
          { timeout: 5000, maximumAge: 0 }
        );
      });
      
      setLocation(prev => ({ ...prev, permissionGranted: true }));
      return true;
    } catch (error) {
      setLocation(prev => ({ ...prev, permissionGranted: false }));
      toast({
        title: 'Location permission required',
        description: 'Please enable location access to receive proximity notifications.',
        variant: 'destructive',
      });
      return false;
    }
  }, [toast]);

  // Auto-start if enabled
  useEffect(() => {
    if (opts.autoStart && user?.id) {
      startTracking();
    }
    
    return () => {
      stopTracking();
    };
  }, [opts.autoStart, user?.id, startTracking, stopTracking]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopTracking();
    };
  }, [stopTracking]);

  return {
    location,
    startTracking,
    stopTracking,
    requestPermission,
    isSupported: typeof navigator !== 'undefined' && 'geolocation' in navigator,
  };
}

// Helper function to calculate distance between two coordinates (Haversine formula)
function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000; // Earth's radius in meters
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in meters
}

function toRad(degrees: number): number {
  return degrees * (Math.PI / 180);
}

